import { styled } from "goober";
import { useEffect, useEffectEvent, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useMouseDownOutside } from "../../../hooks/useMouseDownOutside";
import { zIndexes } from "../../../lib/zIndexes";
import { ClickButton } from "../../ClickButton";
import { Icon, type IconName } from "../../Icon";
import { Text } from "../../Text";
import { Tooltip } from "../../Tooltip";

const MENU_WIDTH = 180;

const Menu = styled("div")`
  position: fixed;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  padding: 4px;
  z-index: ${zIndexes.contextMenu};
  min-width: ${MENU_WIDTH}px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MenuItem = styled(ClickButton)`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  color: var(--text-primary);
  &:hover {
    background: var(--alpha4);
  }
  &[data-destructive="true"] {
    color: var(--red-600);
  }
  &[data-destructive="true"]:hover {
    background: color-mix(in srgb, var(--red-600) 12%, transparent);
  }
  &[data-disabled="true"] {
    opacity: 0.4;
    cursor: not-allowed;
  }
  &[data-disabled="true"]:hover {
    background: transparent;
  }
`;

const Divider = styled("div")`
  height: 0.5px;
  margin: 4px 8px;
  background: var(--border-alpha);
`;

export type MenuAction = {
  key: string;
  icon: IconName;
  label: string;
  destructive?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onClick: () => void;
};

type Props = {
  anchor: HTMLElement;
  side: "top" | "bottom";
  onView: () => void;
  onRemove?: () => void;
  removeLabel: string;
  removeReason?: string;
  actions: MenuAction[];
  onClose: () => void;
};

export function NodeContextMenu({
  anchor,
  side,
  onView,
  onRemove,
  removeLabel,
  removeReason,
  actions,
  onClose,
}: Props) {
  const [containerRef, menuRef] = useMouseDownOutside(onClose, anchor);
  const closeFromScroll = useEffectEvent(() => onClose());

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Keep the menu flush with its opener by writing styles directly on scroll —
  // no React state, so the graph never re-renders mid-scroll.
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const scroll = document.querySelector("[data-ag-scroll]");
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const place = () => {
      const r = anchor.isConnected ? anchor.getBoundingClientRect() : null;
      const sr = scroll?.getBoundingClientRect();
      if (
        !r ||
        !sr ||
        r.bottom < sr.top ||
        r.top > sr.bottom ||
        r.right < sr.left ||
        r.left > sr.right
      ) {
        closeFromScroll();
        return;
      }
      el.style.left = `${Math.max(8, Math.min(r.right - w, window.innerWidth - w - 8))}px`;
      const desiredTop = side === "top" ? r.top - h - 4 : r.bottom + 4;
      el.style.top = `${Math.max(8, Math.min(desiredTop, window.innerHeight - h - 8))}px`;
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [anchor, menuRef, side]);

  const pick = (fn: () => void) => () => {
    fn();
    onClose();
  };

  return createPortal(
    <div ref={containerRef} style={{ display: "contents" }}>
      <Menu ref={menuRef} data-popover>
        <MenuItem onClick={pick(onView)}>
          <Icon name="maximize-2" size={16} />
          <Text variant="text-regular">View</Text>
        </MenuItem>
        {onRemove ? (
          <MenuItem onClick={pick(onRemove)}>
            <Icon name="circle-minus" size={16} />
            <Text variant="text-regular">{removeLabel}</Text>
          </MenuItem>
        ) : (
          <Tooltip content={removeReason ?? ""}>
            <MenuItem data-disabled="true" disabled>
              <Icon name="circle-minus" size={16} />
              <Text variant="text-regular">{removeLabel}</Text>
            </MenuItem>
          </Tooltip>
        )}
        {actions.length > 0 && <Divider />}
        {actions.map((action) =>
          action.disabled ? (
            <Tooltip key={action.key} content={action.disabledReason ?? ""}>
              <MenuItem
                data-destructive={action.destructive ? "true" : undefined}
                data-disabled="true"
                disabled
              >
                <Icon name={action.icon} size={16} />
                <Text variant="text-regular">{action.label}</Text>
              </MenuItem>
            </Tooltip>
          ) : (
            <MenuItem
              key={action.key}
              data-destructive={action.destructive ? "true" : undefined}
              onClick={pick(action.onClick)}
            >
              <Icon name={action.icon} size={16} />
              <Text variant="text-regular">{action.label}</Text>
            </MenuItem>
          )
        )}
      </Menu>
    </div>,
    document.body
  );
}
